import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useHttpConfig,
  useHttpConfigs,
  useHttpEndpoint,
  useUpdateHttpEndpoint,
} from "../../hooks/useHttp";
import { HttpRequestEndpointUpdate } from "../../types/http";
import AuditInfo from "../../components/ui/AuditInfo";
import { Input, Textarea } from "../../components/ui/Input";
import { auditApi } from "../../services/api";

function toJsonString(value: any): string {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return "{}";
  }
}
function safeJson(text?: string) {
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export default function EditHttpEndpointPage() {
  const { id } = useParams<{ id: string }>();
  const endpointId = Number(id);
  const navigate = useNavigate();
  const { data: ep, isLoading } = useHttpEndpoint(endpointId);
  const { data: cfgs } = useHttpConfigs({ page: 1, page_size: 200 });
  const updateMutation = useUpdateHttpEndpoint();
  const [form, setForm] = useState<{
    [K in keyof HttpRequestEndpointUpdate]?: any;
  }>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ep) {
      setForm({
        config_id: ep.config_id,
        name: ep.name,
        path: ep.path,
        method: ep.method || "GET",
        params: toJsonString(ep.params ?? {}),
        headers: toJsonString(ep.headers ?? {}),
        body_template: ep.body_template || "",
        response_schema: toJsonString(ep.response_schema ?? {}),
        description: ep.description || "",
      });
    }
  }, [ep]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateMutation.mutateAsync({
        id: endpointId,
        data: {
          config_id: form.config_id ?? undefined,
          name: form.name || undefined,
          path: form.path || undefined,
          method: form.method || "GET",
          params: safeJson(form.params),
          headers: safeJson(form.headers),
          body_template: form.body_template || undefined,
          response_schema: safeJson(form.response_schema),
          description: form.description || undefined,
        },
      });
      navigate("/http-endpoints");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !ep) {
    return <div className="py-12 text-center">Loading...</div>;
  }

  const configs = cfgs?.configs || [];

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Edit HTTP Endpoint: {ep.name}
        </h2>
        <p className="text-sm text-gray-500">Update endpoint configuration</p>
      </div>
      <form
        className="bg-white shadow sm:rounded-lg p-6 space-y-4"
        onSubmit={handleSubmit}
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Config
          </label>
          <select
            value={form.config_id ?? ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                config_id: e.target.value ? Number(e.target.value) : undefined,
              }))
            }
            className="w-full px-3 py-2 border rounded"
            required
          >
            <option value="">Select config</option>
            {configs.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Name"
              type="text"
              value={form.name || ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              variant="rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Method
            </label>
            <select
              value={form.method || "GET"}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, method: e.target.value }))
              }
              className="w-full px-3 py-2 border rounded"
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>
        </div>
        <div>
          <Input
            label="Path"
            type="text"
            value={form.path || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, path: e.target.value }))
            }
            variant="rounded-md"
            placeholder="/users/{id}"
            required
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Textarea
              label="Params (JSON)"
              value={form.params || ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, params: e.target.value }))
              }
              variant="rounded-md"
              className="font-mono"
              rows={4}
            />
          </div>
          <div>
            <Textarea
              label="Headers (JSON)"
              value={form.headers || ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, headers: e.target.value }))
              }
              variant="rounded-md"
              className="font-mono"
              rows={4}
            />
          </div>
        </div>
        <div>
          <Textarea
            label="Body Template"
            value={form.body_template || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, body_template: e.target.value }))
            }
            variant="rounded-md"
            className="font-mono"
            rows={4}
            placeholder="JSON or string template"
          />
        </div>
        <div>
          <Textarea
            label="Response Schema (JSON)"
            value={form.response_schema || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, response_schema: e.target.value }))
            }
            variant="rounded-md"
            className="font-mono"
            rows={4}
          />
        </div>
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            className="btn-secondary px-4 py-2 rounded-md"
            onClick={() => navigate("/http-endpoints")}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary px-4 py-2 rounded-md"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
      {ep?.id ? (
        <AuditInfo
          resource="http-endpoints"
          id={ep.id}
          fetch={auditApi.getLastChange}
        />
      ) : null}
    </div>
  );
}
